import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { WowImage } from '.';
import { extractWixMediaId } from './utils';
import { wowImageDriverFactory } from './WowImage.driver';
import { ImageResizeOptions } from '../Image/types';

const defaultProps = {
  src: 'c5f754_dd75514d14fa4057b4f4a6cc8ce7add3~mv2.jpg',
  width: 480,
  height: 360,
  sourceWidth: 480,
  sourceHeight: 360,
};

describe('Image', () => {
  const createDriver = createRendererWithUniDriver(wowImageDriverFactory);

  const sampleSources = {
    absolute:
      'https://m.media-amazon.com/images/M/MV5BZGMwOGIwZjUtOWQ1OS00YWRjLWJmZGMtN2Y1OWQ3ZDYwYTM3XkEyXkFqcGdeQXVyNzU1NzE3NTg@._V1_.jpg',
    relative: 'c5f754_dd75514d14fa4057b4f4a6cc8ce7add3~mv2.jpg',
  };
  const sampleDimensions = { width: 480, height: 360 };
  const sampleAlt = 'Garfield smiles and puts his hand over chest';

  describe('absolute', () => {
    const src = sampleSources.absolute;

    it('should return `src` as is even when providing an unsecured URL', async () => {
      const unsecuredSrc = src.replace('https://', 'http://');
      const { driver } = createDriver(<WowImage src={unsecuredSrc} />);

      expect(await driver.getSrc()).toBe(unsecuredSrc);
    });

    it('should extract wix media id from wixstatic', async () => {
      const { driver } = createDriver(
        <WowImage
          src="https://static.wixstatic.com/media/iamanid~mv2.jpg/v1/fit/al_c/file.jpg"
          {...sampleDimensions}
        />,
      );

      expect(await driver.getSrc()).toBe('iamanid~mv2.jpg');
    });
  });

  describe.each(Object.keys(sampleSources))('%s', (sourceType) => {
    const src = sampleSources[sourceType];

    it('should render', async () => {
      const { driver } = createDriver(
        <WowImage src={src} {...sampleDimensions} />,
      );

      expect(await driver.exists()).toBe(true);
    });

    it('should return `src` as is', async () => {
      const { driver } = createDriver(
        <WowImage src={src} {...sampleDimensions} />,
      );

      expect(await driver.getSrc()).toBe(src);
    });

    it('should return `alt`', async () => {
      const { driver } = createDriver(
        <WowImage src={src} {...sampleDimensions} alt={sampleAlt} />,
      );

      expect(await driver.getAlt()).toBe(sampleAlt);
    });

    it('should throw an error when invoking `isLoaded`', () => {
      const { driver } = createDriver(
        <WowImage src={src} {...sampleDimensions} alt={sampleAlt} />,
      );

      expect(driver.isLoaded).toThrow();
    });

    it('should throw an error when invoking `isError`', () => {
      const { driver } = createDriver(
        <WowImage src={src} {...sampleDimensions} alt={sampleAlt} />,
      );

      expect(driver.isLoaded).toThrow();
    });
  });

  describe('aspectRatio', () => {
    it('should be able to get aspect ratio as null [when] not given', async () => {
      const { driver } = createDriver(<WowImage {...defaultProps} />);
      expect(await driver.getAspectRatio()).toBe(null);
    });

    it('should be able to get aspect ratio [when] given one', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} aspectRatio="cinema" />,
      );
      expect(await driver.getAspectRatio()).toBe('cinema');
    });
  });

  describe('sourceWidth', () => {
    it('should be able to get sourceWith as null [when] not given', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} sourceWidth={undefined} />,
      );
      expect(await driver.getSourceWidth()).toBe(null);
    });

    it('should be able to get sourceWidth [when] given one', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} sourceWidth={500} />,
      );
      expect(await driver.getSourceWidth()).toBe('500');
    });
  });

  describe('sourceHeight', () => {
    it('should be able to get sourceHeight as null [when] not given', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} sourceHeight={undefined} />,
      );
      expect(await driver.getSourceHeight()).toBe(null);
    });

    it('should be able to get sourceHeight [when] given one', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} sourceHeight={500} />,
      );
      expect(await driver.getSourceHeight()).toBe('500');
    });
  });

  describe('resize', () => {
    it('should be able to get resize as contain [when] not given', async () => {
      const { driver } = createDriver(<WowImage {...defaultProps} />);
      expect(await driver.getResize()).toBe('contain');
    });

    it('should be able to get resize [when] given cover', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} resize={ImageResizeOptions.cover} />,
      );
      expect(await driver.getResize()).toBe('cover');
    });
  });

  describe('shouldUseLQIP', () => {
    it('should be able to get shouldUseLQIP [when] given true', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} shouldUseLQIP />,
      );
      expect(await driver.getShouldUseLQIP()).toBe('true');
    });

    it('should be able to get shouldUseLQIP [when] given false', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} shouldUseLQIP={false} />,
      );
      expect(await driver.getShouldUseLQIP()).toBe('false');
    });

    it('should not be able to get shouldUseLQIP [when] not given', async () => {
      const { driver } = createDriver(<WowImage {...defaultProps} />);
      expect(await driver.getShouldUseLQIP()).toBe(null);
    });
  });

  describe('encoding', () => {
    it('should use avif encoding [when] given true', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} encoding="AVIF" />,
      );
      expect(await driver.getEncoding()).toBe('AVIF');
    });

    it('should not use avif encoding [when] not given', async () => {
      const { driver } = createDriver(<WowImage {...defaultProps} />);
      expect(await driver.getEncoding()).toBeUndefined();
    });
  });

  describe('isInFirstFold', () => {
    it('should be able to get isInFirstFold [when] given true', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} isInFirstFold />,
      );
      expect(await driver.getIsInFirstFold()).toBe('true');
    });

    it('should be able to get isInFirstFold [when] given false', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} isInFirstFold={false} />,
      );
      expect(await driver.getIsInFirstFold()).toBe('false');
    });

    it('should not be able to get isInFirstFold [when] not given', async () => {
      const { driver } = createDriver(<WowImage {...defaultProps} />);
      expect(await driver.getIsInFirstFold()).toBe(null);
    });
  });

  describe('isSEOBot', () => {
    it('should be able to get isSEOBot [when] given true', async () => {
      const { driver } = createDriver(<WowImage {...defaultProps} isSEOBot />);
      expect(await driver.getIsSEOBot()).toBe('true');
    });

    it('should be able to get isSEOBot [when] given false', async () => {
      const { driver } = createDriver(
        <WowImage {...defaultProps} isSEOBot={false} />,
      );
      expect(await driver.getIsSEOBot()).toBe('false');
    });

    it('should not be able to get isSEOBot [when] not given', async () => {
      const { driver } = createDriver(<WowImage {...defaultProps} />);
      expect(await driver.getIsSEOBot()).toBe(null);
    });
  });

  describe('utils', () => {
    describe('extractWixMediaId', () => {
      it('should extract wix media id', () => {
        const src =
          'https://static.wixstatic.com/media/iamanid~mv2.jpg/v1/fit/al_c/file.jpg';
        expect(extractWixMediaId(src)).toBe('iamanid~mv2.jpg');

        const src2 =
          'https://static.wixstatic.com/media/1bf8c6_ca5bda9d802e47fa8a0c7ab6eb922a0a%7Emv2.jpg/v1/fill/w_91,h_91,al_c,q_90/1bf8c6_ca5bda9d802e47fa8a0c7ab6eb922a0a%7Emv2.jpg';
        expect(extractWixMediaId(src2)).toBe(
          '1bf8c6_ca5bda9d802e47fa8a0c7ab6eb922a0a%7Emv2.jpg',
        );
      });
      it('should return null when url not recognized', () => {
        expect(extractWixMediaId(sampleSources.absolute)).toBe(null);
      });
    });
  });
});
